// 5×7 bitmap font for the CRT and the housing labels (TERMINAL.md §9.4).
// Each glyph is 7 rows of 5 cells ('#' lit), rows separated by '/'.

export const GLYPH_W = 5;
export const GLYPH_H = 7;
export const GLYPH_GAP = 1;

const GLYPHS: Record<string, string> = {
  A: '.###./#...#/#...#/#####/#...#/#...#/#...#',
  B: '####./#...#/#...#/####./#...#/#...#/####.',
  C: '.###./#...#/#..../#..../#..../#...#/.###.',
  D: '####./#...#/#...#/#...#/#...#/#...#/####.',
  E: '#####/#..../#..../####./#..../#..../#####',
  F: '#####/#..../#..../####./#..../#..../#....',
  G: '.###./#...#/#..../#.###/#...#/#...#/.####',
  H: '#...#/#...#/#...#/#####/#...#/#...#/#...#',
  I: '.###./..#../..#../..#../..#../..#../.###.',
  J: '..###/...#./...#./...#./...#./#..#./.##..',
  K: '#...#/#..#./#.#../##.../#.#../#..#./#...#',
  L: '#..../#..../#..../#..../#..../#..../#####',
  M: '#...#/##.##/#.#.#/#.#.#/#...#/#...#/#...#',
  N: '#...#/#...#/##..#/#.#.#/#..##/#...#/#...#',
  O: '.###./#...#/#...#/#...#/#...#/#...#/.###.',
  P: '####./#...#/#...#/####./#..../#..../#....',
  Q: '.###./#...#/#...#/#...#/#.#.#/#..#./.##.#',
  R: '####./#...#/#...#/####./#.#../#..#./#...#',
  S: '.####/#..../#..../.###./....#/....#/####.',
  T: '#####/..#../..#../..#../..#../..#../..#..',
  U: '#...#/#...#/#...#/#...#/#...#/#...#/.###.',
  V: '#...#/#...#/#...#/#...#/#...#/.#.#./..#..',
  W: '#...#/#...#/#...#/#.#.#/#.#.#/#.#.#/.#.#.',
  X: '#...#/#...#/.#.#./..#../.#.#./#...#/#...#',
  Y: '#...#/#...#/.#.#./..#../..#../..#../..#..',
  Z: '#####/....#/...#./..#../.#.../#..../#####',
  '0': '.###./#...#/#..##/#.#.#/##..#/#...#/.###.',
  '1': '..#../.##../..#../..#../..#../..#../.###.',
  '2': '.###./#...#/....#/...#./..#../.#.../#####',
  '3': '####./....#/....#/.###./....#/....#/####.',
  '4': '...#./..##./.#.#./#..#./#####/...#./...#.',
  '5': '#####/#..../####./....#/....#/#...#/.###.',
  '6': '.###./#..../#..../####./#...#/#...#/.###.',
  '7': '#####/....#/...#./..#../.#.../.#.../.#...',
  '8': '.###./#...#/#...#/.###./#...#/#...#/.###.',
  '9': '.###./#...#/#...#/.####/....#/....#/.###.',
  '!': '..#../..#../..#../..#../..#../...../..#..',
  '.': '...../...../...../...../...../...../..#..',
  ':': '...../..#../...../...../...../..#../.....',
  '/': '....#/...#./...#./..#../.#.../.#.../#....',
  '-': '...../...../...../.###./...../...../.....',
  '+': '...../..#../..#../#####/..#../..#../.....',
  '%': '##..#/##..#/...#./..#../.#.../#..##/#..##',
  '?': '.###./#...#/....#/...#./..#../...../..#..',
  "'": '..#../..#../...../...../...../...../.....',
  '*': '...../#.#.#/.###./#####/.###./#.#.#/.....',
  ' ': '...../...../...../...../...../...../.....',
  '>': '.#.../..#../...#./....#/...#./..#../.#...',
  '<': '...#./..#../.#.../#..../.#.../..#../...#.',
  ',': '...../...../...../...../...../..#../.#...',
  '(': '...#./..#../.#.../.#.../.#.../..#../...#.',
  ')': '.#.../..#../...#./...#./...#./..#../.#...',
  '=': '...../...../#####/...../#####/...../.....',
};

const cache = new Map<string, readonly string[]>();

export interface PixelSink {
  fillStyle: unknown;
  fillRect(x: number, y: number, w: number, h: number): void;
}

/** Rows of a glyph; lowercase maps to uppercase, unknown characters to '?'. */
export function glyphRows(ch: string): readonly string[] {
  const key = GLYPHS[ch.toUpperCase()] !== undefined ? ch.toUpperCase() : '?';
  let rows = cache.get(key);
  if (!rows) {
    rows = (GLYPHS[key] as string).split('/');
    cache.set(key, rows);
  }
  return rows;
}

export function measureText(text: string, scale = 1): number {
  const n = [...text].length;
  return n === 0 ? 0 : (n * (GLYPH_W + GLYPH_GAP) - GLYPH_GAP) * scale;
}

/** Greedy word wrap to `cols` characters. */
export function wrapText(text: string, cols: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/)) {
    if (!word) continue;
    const next = line ? `${line} ${word}` : word;
    if (next.length > cols && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export function drawText(sink: PixelSink, text: string, x: number, y: number, scale: number, color: string): void {
  sink.fillStyle = color;
  let cx = x;
  for (const ch of text) {
    const rows = glyphRows(ch);
    for (let r = 0; r < GLYPH_H; r++) {
      const row = rows[r] as string;
      for (let c = 0; c < GLYPH_W; c++) {
        if (row[c] === '#') sink.fillRect(cx + c * scale, y + r * scale, scale, scale);
      }
    }
    cx += (GLYPH_W + GLYPH_GAP) * scale;
  }
}
